// 图解规则（作者 2026-11-02：“规则和设置界面全是文字，可以做图文版”）：每条规则配真牌面示例。
// 口径同 src/game/ruleset.ts 与竞赛规则；示例牌用 cardFace 画，和手里的牌同一套牌面。
import type { Card, Suit } from './game/cards.ts';

let nid = 90000;
const c = (s: string): Card => {
  if (s === '大王' || s === '小王') return { id: nid++, suit: 'J', rank: s === '大王' ? 17 : 16 }; // 王用中文写，免得和 ♠J（SJ）混淆
  const map: Record<string, number> = { J: 11, Q: 12, K: 13, A: 14 };
  return { id: nid++, suit: s[0] as Suit, rank: map[s.slice(1)] ?? Number(s.slice(1)) };
};
const hand = (s: string) => s.split(' ').map(c);

export interface GuideItem { title: string; text: string; cards?: Card[][]; arrows?: string[]; level?: number }
export interface GuideSection { title: string; items: GuideItem[] }

/** level：示例里的本副级牌（用来显示“级”“配”角标）。 */
export const GUIDE: GuideSection[] = [
  {
    title: '一、谁和谁一队',
    items: [{ title: '四人两队，对家是搭档', text: '你和对家（老顾）一队，上家（苏小算）和下家（雷哥）一队；逆时针出牌，下家在你右手边。两副牌 108 张，每人 27 张。', arrows: ['seats'] }],
  },
  {
    title: '二、级牌与逢人配',
    items: [
      { title: '级牌', text: '本副打几，几就是级牌（例：打 2）。单张、对子里，级牌比 A 大、比小王小；在顺子里按原点数。', cards: [hand('S2 C2 D2')], level: 2 },
      { title: '逢人配', text: '红桃级牌叫“逢人配”，可以当除大小王外的任何牌，凑炸弹、顺子都行。', cards: [hand('H2'), hand('S9 D9 C9 H2')], arrows: ['', '= 9 9 9 9 炸弹'], level: 2 },
    ],
  },
  {
    title: '三、十种牌型',
    items: [
      { title: '单张', text: '任意一张。', cards: [hand('SK')] },
      { title: '对子', text: '两张同点；两张大王或两张小王也是对子。', cards: [hand('S8 D8'), hand('大王 大王')] },
      { title: '三同张', text: '三张同点。', cards: [hand('S6 H6 C6')] },
      { title: '三带对', text: '三张带一对，只比三张的大小。', cards: [hand('SJ HJ CJ S4 D4')] },
      { title: '顺子', text: '正好 5 张连续单牌，A 可当 1（A2345）或最大（10JQKA），不能转角。', cards: [hand('S3 D4 C5 H6 S7'), hand('S10 HJ CQ DK SA')] },
      { title: '三连对', text: '三个连续的对子。', cards: [hand('S5 D5 C6 H6 S7 D7')] },
      { title: '三同连张（钢板）', text: '两个连续的三张。', cards: [hand('S9 D9 C9 H10 S10 D10')] },
      { title: '炸弹', text: '四张及以上同点，最多十张；张数多的大。', cards: [hand('S7 D7 C7 H7')] },
      { title: '同花顺', text: '同一花色的顺子，算炸弹。', cards: [hand('H8 H9 H10 HJ HQ')] },
      { title: '四大天王', text: '两张大王加两张小王，最大的牌。', cards: [hand('大王 大王 小王 小王')] },
    ],
  },
  {
    title: '四、谁压得住谁',
    items: [{
      title: '炸弹的大小',
      text: '从大到小：四大天王 ＞ 六张及以上炸弹 ＞ 同花顺 ＞ 五张炸弹 ＞ 四张炸弹 ＞ 其他所有牌型。普通牌型之间只能同牌型、同张数比大小。',
      cards: [hand('大王 大王 小王 小王'), hand('S3 D3 C3 H3 S3 D3'), hand('H8 H9 H10 HJ HQ'), hand('SA DA CA HA SA'), hand('SK DK CK HK')],
      arrows: ['＞', '＞', '＞', '＞', ''],
    }],
  },
  {
    title: '五、出牌、报牌与借风',
    items: [
      { title: '一圈怎么走', text: '领出的人出任意牌型，后面的人要么压过去，要么点「不出」；三家都不出，最后出牌的人接着领出。' },
      { title: '报牌', text: '手里剩 10 张以内要亮出张数（头像旁的绿色“报牌 N”），多于 10 张别人看不到张数。' },
      { title: '借风', text: '上游或二游出完最后一手、其他人都不要时，由他的搭档接着领出。' },
    ],
  },
  {
    title: '六、升级：只有上游一方升',
    items: [{ title: '看搭档第几个出完', text: '上游的搭档二游升 3 级、三游升 2 级、下游升 1 级。另外两家都没出完就结束时叫“双下”。点顶栏的比分可以随时算分。', arrows: ['upgrade'] }],
  },
  {
    title: '七、进贡、还牌与抗贡（第二副起）',
    items: [
      { title: '进贡', text: '上一副的下游把手里最大的一张（红桃级牌除外）交给上游。', cards: [hand('SA')], arrows: ['下游 → 上游'] },
      { title: '还牌', text: '上游还一张牌点不超过 10 的牌（级牌不能还）；手里都大于 10 就还最小的。由进贡的人先出。', cards: [hand('C4')], arrows: ['上游 → 下游'] },
      { title: '抗贡', text: '下游（双下时两人合计）手里有两个大王，就不用进贡，由上游先出。', cards: [hand('大王 大王')] },
    ],
  },
  {
    title: '八、过 A：问鼎武林',
    items: [{ title: 'A 级必打', text: '打到 A 后，打自己的 A 时拿上游、且搭档不是下游，就过 A，赢下这一轮。开局可选“限 8 / 12 副”，到时比级数；也可选短局“打到过 10 / 过 6”，规则同过 A，只是终点提前（顶栏“目标”格写着过几）。' }],
  },
  {
    title: '九、一键理牌与换方案',
    items: [
      { title: '一键理牌', text: '点右下「一键理牌」，自动把手牌理成炸弹、同花顺、整手牌型和散牌，每组上方标出牌型名；再点一次就重新理一遍。想按点数排，点「按点数」。', cards: [hand('S8 H8 C8 D8'), hand('S3 D4 C5 H6 S7')], arrows: ['四炸', '顺子'] },
      { title: '换方案 ⇄', text: '签上带「⇄」的，说明这手牌还有别的理法（比如逢人配拿去凑同花顺，或者补成更大的炸弹）。点一下换下一种，其余牌跟着重排；鼠标停在签上能看到每种方案的区别。' },
      { title: '逢人配去向', text: '逢人配右上角写「配→8」，表示它在这一组里当 8 用；写「配·本色」表示按红桃级牌本身用。', cards: [hand('S8 H8 C8 D8 H2')], arrows: ['配→8：五炸'], level: 2 },
      { title: '理牌的原则', text: '炸弹不拆；逢人配放在最划算的地方（凑同花顺、补大炸弹，或者留着）；剩下的牌尽量理成出完要的手数最少。' },
    ],
  },
];
